import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Edit3, MessageSquare, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  useApproveComment,
  useDeleteComment,
  useGetCommentsByInvestigation,
  useUpdateComment,
} from '@/application/services/comment.service'
import { Badge } from '@/ui/components/common/Badge'
import { Button } from '@/ui/components/common/Button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/ui/components/common/Dialog'
import { Skeleton } from '@/ui/components/common/Skeleton'
import { Textarea } from '@/ui/components/common/Textarea'

/**
 * Moderated comments thread for an investigation (article): list of
 * public-site comments and replies with approve / edit / delete actions,
 * plus the edit-comment dialog.
 *
 * Owns: comments fetch, approve/update/delete mutations, edit-dialog state.
 */
export function InvestigationCommentsSection({ investigationId }: { investigationId: string }) {
  const { t } = useTranslation()

  const [editCommentId, setEditCommentId] = useState<string | null>(null)
  const [editCommentContent, setEditCommentContent] = useState('')

  const { data: comments, isLoading: isLoadingComments } =
    useGetCommentsByInvestigation(investigationId)
  const { mutateAsync: approveComment } = useApproveComment(investigationId)
  const { mutateAsync: updateComment } = useUpdateComment(investigationId)
  const { mutateAsync: deleteComment } = useDeleteComment(investigationId)

  const handleApproveComment = async (commentId: string) => {
    try {
      await approveComment(commentId)
      toast.success(t('article_comments.approved_toast'))
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : String(error))
    }
  }

  const handleDeleteComment = async (commentId: string) => {
    if (window.confirm(t('article_comments.delete_confirm'))) {
      try {
        await deleteComment(commentId)
        toast.success(t('article_comments.deleted_toast'))
      } catch (error: unknown) {
        toast.error(error instanceof Error ? error.message : String(error))
      }
    }
  }

  const handleEditComment = (comment: { commentId: string; content: string }) => {
    setEditCommentId(comment.commentId)
    setEditCommentContent(comment.content)
  }

  const handleUpdateComment = async () => {
    if (!editCommentId) return
    try {
      await updateComment({ commentId: editCommentId, content: editCommentContent })
      toast.success(t('article_comments.updated_toast'))
      setEditCommentId(null)
      setEditCommentContent('')
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : String(error))
    }
  }

  return (
    <>
      <div>
        <h2 className="font-serif text-xl font-bold mb-6 flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-primary" />
          {t('article_comments.title')}
          {comments && (
            <Badge variant="secondary" className="ml-1">
              {comments.length}
            </Badge>
          )}
        </h2>

        {/* Info: commentaires depuis le site uniquement */}
        <div className="mb-4 px-3 py-2 rounded-lg bg-muted/40 border border-border text-xs text-muted-foreground">
          Les commentaires sont soumis depuis le site public et peuvent être modérés ici.
        </div>

        {isLoadingComments ? (
          <div className="space-y-4">
            {Array.from({ length: 2 }).map((_item, index) => (
              <Skeleton key={index} className="h-20 w-full rounded-lg" />
            ))}
          </div>
        ) : !comments || comments.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-30" />
            <p>{t('article_comments.no_comments')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {comments.map((comment) => (
              <div
                key={comment.commentId}
                className={`bg-card border border-border rounded-lg p-4 ${comment.parentId ? 'ml-6 sm:ml-10' : ''}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="h-7 w-7 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary">
                      {comment.username.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-sm font-medium">{comment.username}</span>
                    {comment.status === 'pending' && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-500/10 text-amber-600 border border-amber-500/20">
                        {t('article_comments.pending')}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-muted-foreground mr-2">
                      {new Date(comment.createdAt).toLocaleDateString('fr-FR', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </span>
                    {comment.status === 'pending' && (
                      <button
                        onClick={() => handleApproveComment(comment.commentId)}
                        className="p-1 rounded hover:bg-emerald-500/10 text-emerald-600 transition-colors"
                        title={t('article_comments.approve')}
                      >
                        <Check className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      onClick={() => handleEditComment(comment)}
                      className="p-1 rounded hover:bg-muted/50 text-muted-foreground transition-colors"
                      title={t('article_comments.edit')}
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDeleteComment(comment.commentId)}
                      className="p-1 rounded hover:bg-destructive/10 text-destructive transition-colors"
                      title={t('article_comments.delete')}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <p className="text-sm text-foreground/80 leading-relaxed">{comment.content}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Comment Dialog */}
      <Dialog
        open={!!editCommentId}
        onOpenChange={(open) => {
          if (!open) {
            setEditCommentId(null)
            setEditCommentContent('')
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif">
              {t('article_comments.edit_dialog_title')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <Textarea
              value={editCommentContent}
              onChange={(event) => setEditCommentContent(event.target.value)}
              rows={4}
              className="resize-none"
            />
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline">
                  {t('common.cancel')}
                </Button>
              </DialogClose>
              <Button type="button" onClick={handleUpdateComment}>
                {t('article_comments.edit_submit')}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
